import { useEffect, useRef, type ReactNode } from "react";

type ScrollRevealProps = {
  children: ReactNode;
  delay?: 0 | 1 | 2 | 3;
  className?: string;
  /** Render `as` element. Defaults to a div. */
  as?: "div" | "section" | "article" | "header";
  /** When true, the element starts visible without waiting for intersection. */
  immediate?: boolean;
};

/**
 * Wraps children in a div that fades in + slides up when scrolled into view.
 * Uses IntersectionObserver — animation only triggers once.
 */
export function ScrollReveal({
  children,
  delay = 0,
  className = "",
  as: Tag = "div",
  immediate,
}: ScrollRevealProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (immediate) {
      el.classList.add("is-visible");
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        }
      },
      { rootMargin: "-10% 0px -10% 0px", threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [immediate]);

  const delayClass =
    delay === 1
      ? "reveal-delay-1"
      : delay === 2
        ? "reveal-delay-2"
        : delay === 3
          ? "reveal-delay-3"
          : "";

  return (
    <Tag
      ref={ref as React.RefObject<HTMLDivElement>}
      className={`reveal ${delayClass} ${className}`.trim()}
    >
      {children}
    </Tag>
  );
}
